import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    TagIcon,
    Squares2X2Icon,
    ShoppingBagIcon,
    XMarkIcon,
    Bars3Icon,
    HomeIcon,
    UserIcon,
    ArrowRightOnRectangleIcon,
    BellIcon,
    ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useAppContext } from '../context/AppContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../images/FINAL-DESIGN-LOGO.png';
import { API } from '../config';
import ProductCard from '../components/ProductCard';
import ProductDetailModal from '../components/ProductDetailModal';

const ProductsPage = () => {
    const [searchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category_id') || '');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [subcategories, setSubcategories] = useState([]);
    const [expandedCategory, setExpandedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const { user, cart, logout } = useAppContext();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const navigate = useNavigate();

    const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        if (selectedCategory) {
            fetchSubcategories(selectedCategory);
        } else {
            setSubcategories([]);
        }
    }, [selectedCategory, selectedSubcategory, searchTerm]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category_id', selectedCategory);
            if (selectedSubcategory) params.append('subcategory_id', selectedSubcategory);
            if (searchTerm) params.append('search', searchTerm);

            const response = await axios.get(`${API}/products?${params}`);
            setProducts(response.data);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await axios.get(`${API}/subcategories`, {
                params: { category_id: categoryId }
            });
            setSubcategories(response.data);
        } catch (error) {
            console.error('Failed to fetch subcategories:', error);
            setSubcategories([]);
        }
    };

    const handleQuickView = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="flex w-full">
                {/* Mobile Sidebar Overlay */}
                {isSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <aside className={`
                    fixed lg:sticky top-0 left-0 z-50
                    w-64 bg-[#111114] text-gray-400 flex flex-col
                    h-screen
                    transform transition-transform duration-300 ease-in-out
                    ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}>
                    <div className="flex flex-col h-full">
                        <div className="p-6 flex-1 overflow-y-auto">
                            {/* Logo Area */}
                            <div className="flex items-center gap-3 mb-10 px-2">
                                <img src={logo} alt="Illustra" className="h-8 w-auto brightness-0 invert" />
                            </div>

                            <nav className="space-y-2">
                                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Menu</p>

                                <button
                                    onClick={() => setSelectedCategory('')}
                                    className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${selectedCategory === ''
                                        ? 'bg-[#1F1F23] text-white shadow-lg'
                                        : 'hover:bg-[#1F1F23] hover:text-white'
                                        }`}
                                >
                                    <Squares2X2Icon className="h-5 w-5 mr-3" />
                                    <span className="font-medium">All Products</span>
                                </button>

                                <div className="pt-4">
                                    <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Categories</p>
                                    {categories.map((category) => (
                                        <div key={category.id}>
                                            <button
                                                onClick={() => {
                                                    if (selectedCategory === category.id) {
                                                        setExpandedCategory(expandedCategory === category.id ? '' : category.id);
                                                    } else {
                                                        setSelectedCategory(category.id);
                                                        setExpandedCategory(category.id);
                                                        setSelectedSubcategory('');
                                                    }
                                                }}
                                                className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${selectedCategory === category.id
                                                    ? 'bg-[#1F1F23] text-white shadow-lg'
                                                    : 'hover:bg-[#1F1F23] hover:text-white'
                                                    }`}
                                            >
                                                <TagIcon className="h-5 w-5 mr-3" />
                                                <span className="font-medium">{category.name}</span>
                                            </button>

                                            {expandedCategory === category.id && subcategories.length > 0 && (
                                                <div className="ml-12 mt-2 space-y-1 border-l-2 border-gray-800 pl-4">
                                                    <button
                                                        onClick={() => setSelectedSubcategory('')}
                                                        className={`w-full text-left text-sm py-2 transition-colors ${selectedSubcategory === ''
                                                            ? 'text-white font-medium'
                                                            : 'text-gray-500 hover:text-white'
                                                            }`}
                                                    >
                                                        All {category.name}
                                                    </button>
                                                    {subcategories.map((sub) => (
                                                        <button
                                                            key={sub.id}
                                                            onClick={() => setSelectedSubcategory(sub.id)}
                                                            className={`w-full text-left text-sm py-2 transition-colors ${selectedSubcategory === sub.id
                                                                ? 'text-white font-medium'
                                                                : 'text-gray-500 hover:text-white'
                                                                }`}
                                                        >
                                                            {sub.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="pt-4 mt-4 border-t border-gray-800">
                                    <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Main Site</p>
                                    <a href="/" className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1F1F23] rounded-xl transition-all">
                                        <HomeIcon className="h-5 w-5 mr-3" />
                                        <span className="font-medium">Home</span>
                                    </a>
                                </div>
                            </nav>
                        </div>

                        {/* User Profile Section at Bottom */}
                        {user && (
                            <div className="p-4 border-t border-gray-800 bg-[#0c0c0e]">
                                <div className="relative">
                                    <button
                                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                                        className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[#1F1F23] transition-colors group"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-yellow-900 font-bold text-sm shrink-0">
                                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                        </div>
                                        <div className="flex-1 text-left overflow-hidden">
                                            <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        <ChevronUpIcon className={`h-5 w-5 text-gray-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {isProfileOpen && (
                                        <div className="absolute bottom-full left-0 w-full mb-2 bg-[#1F1F23] border border-gray-800 rounded-xl shadow-xl overflow-hidden p-1">
                                            <Link
                                                to="/profile"
                                                className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2f] hover:text-white rounded-lg transition-colors"
                                            >
                                                <UserIcon className="h-5 w-5" />
                                                Profile
                                            </Link>
                                            <button
                                                onClick={logout}
                                                className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-400 hover:bg-[#2a2a2f] hover:text-red-300 rounded-lg transition-colors"
                                            >
                                                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                                                Sign Out
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 w-full flex flex-col h-screen overflow-hidden">
                    {/* Top Header Bar */}
                    <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-8 sticky top-0 z-30">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsSidebarOpen(true)}
                                className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <Bars3Icon className="h-6 w-6" />
                            </button>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">Collections</h1>
                                <p className="text-sm text-gray-500 hidden sm:block">Explore our latest products</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-6">
                            {/* Search Bar */}
                            <div className="relative hidden md:block w-64 lg:w-96">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-[#E31E24]/20 text-sm"
                                />
                            </div>

                            {/* User Actions */}
                            <div className="flex items-center gap-4">
                                <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                    <BellIcon className="h-6 w-6" />
                                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
                                </button>




                                {user ? (
                                    // User profile moved to sidebar
                                    null
                                ) : (
                                    <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-[#E31E24]">
                                        Sign In
                                    </Link>
                                )}
                            </div>
                        </div>
                    </header>

                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto p-4 lg:p-8">
                        {/* Mobile Search (visible only on mobile) */}
                        <div className="md:hidden mb-6">
                            <div className="relative">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl"
                                />
                            </div>
                        </div>

                        {/* Products Grid */}
                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E31E24]"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                {products.map((product) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        onQuickView={handleQuickView}
                                    />
                                ))}
                            </div>
                        )}

                        {!loading && products.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-gray-500 text-lg">No products found matching your criteria.</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            <ProductDetailModal
                product={selectedProduct}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

export default ProductsPage;
